/**
 * The seller's to-do list.
 *
 * P25009-385 BR-2: "Each row states what it is, why it matters, and has exactly
 * one button." BR-3: rows clear themselves once resolved. BR-4: while any urgent
 * item is open, a one-line reminder appears on every seller page.
 *
 * This module is pure: it takes a snapshot of what the app already knows and
 * returns rows. No fetching, no formatting of money, no translation. That keeps
 * the ordering and the "when does this row disappear?" rules testable, which is
 * the part that actually decides whether a seller trusts the list.
 *
 * Rows are DERIVED, never stored. A row disappears because the condition that
 * created it is no longer true — so there is no separate "dismiss" state to get
 * out of sync with reality, and BR-3 is satisfied by construction rather than by
 * remembering to delete something.
 */

/** Row severity. Only `red` promotes the reminder banner onto other pages. */
export const SEVERITY = {
  RED: 'red',
  AMBER: 'amber',
};

/* Lower sorts first. Severity decides the group; within a group this fixed order
 * decides the row, so the list never reshuffles between renders for reasons the
 * seller cannot see. Money the seller cannot receive outranks everything. */
const SEVERITY_RANK = { [SEVERITY.RED]: 0, [SEVERITY.AMBER]: 1 };

/**
 * Build the to-do rows.
 *
 * @param {object} input
 * @param {object|null} input.stripeStatus  /api/stripe-account-status payload
 * @param {boolean}     input.stripeConnected  currentUser.attributes.stripeConnected
 * @param {number|null} input.availableBalanceCents  money waiting to be paid out
 * @param {number}      input.newOrderCount     sales the seller has not acted on
 * @param {number}      input.unreadMessageCount
 * @param {number}      input.lowStockCount     listings at or below 1 in stock
 * @param {number}      input.draftCount        unfinished listings
 * @param {boolean}     input.hasPublishedListing
 * @returns {Array<{id,severity,reasonKey,reasonValues,actionKey,route,routeParams,search}>}
 */
export const buildActionRows = (input = {}) => {
  const {
    stripeStatus = null,
    stripeConnected = null,
    availableBalanceCents = null,
    newOrderCount = 0,
    unreadMessageCount = 0,
    lowStockCount = 0,
    draftCount = 0,
    hasPublishedListing = true,
  } = input;

  const rows = [];

  /* ---- Payouts blocked -------------------------------------------------
   * The headline row from the ticket: "Verifiera Stripe-kontot — Blockerar:
   * utbetalning på 8 787 kr → Starta".
   *
   * Two distinct problems, one row at a time:
   *   - no connected account at all
   *   - an account that still needs information before Stripe will pay out
   *
   * `stripeConnected === false` rather than a falsy check: it is undefined until
   * the current user loads, and a half-loaded page must not tell a seller who
   * already has an account that they have none. Same reasoning as
   * needsAccountSetup() in stripeOnboardingStatus.js. */
  const hasBlockedBalance = typeof availableBalanceCents === 'number' && availableBalanceCents > 0;

  if (stripeConnected === false) {
    rows.push({
      id: 'stripe-missing',
      severity: SEVERITY.RED,
      titleKey: 'SellerDashboardPage.todo.stripeMissing.title',
      reasonKey: hasBlockedBalance
        ? 'SellerDashboardPage.todo.stripeMissing.reasonWithBalance'
        : 'SellerDashboardPage.todo.stripeMissing.reason',
      reasonValues: hasBlockedBalance ? { amount: availableBalanceCents } : {},
      actionKey: 'SellerDashboardPage.todo.stripeMissing.action',
      route: 'StripePayoutPage',
    });
  } else if (stripeStatus && needsStripeInfo(stripeStatus)) {
    rows.push({
      id: 'stripe-incomplete',
      severity: SEVERITY.RED,
      titleKey: 'SellerDashboardPage.todo.stripeIncomplete.title',
      reasonKey: hasBlockedBalance
        ? 'SellerDashboardPage.todo.stripeIncomplete.reasonWithBalance'
        : 'SellerDashboardPage.todo.stripeIncomplete.reason',
      reasonValues: hasBlockedBalance ? { amount: availableBalanceCents } : {},
      actionKey: 'SellerDashboardPage.todo.stripeIncomplete.action',
      route: 'StripePayoutPage',
    });
  }

  /* ---- Orders waiting -------------------------------------------------
   * Red rather than amber: a buyer has paid and is waiting. This is the one
   * thing on the page with someone else's money and patience attached. */
  if (newOrderCount > 0) {
    rows.push({
      id: 'new-orders',
      severity: SEVERITY.RED,
      titleKey: 'SellerDashboardPage.todo.newOrders.title',
      reasonKey: 'SellerDashboardPage.todo.newOrders.reason',
      reasonValues: { count: newOrderCount },
      actionKey: 'SellerDashboardPage.todo.newOrders.action',
      /* P25009-390 BR-5 — an order row opens the ORDER list, not the message
       * inbox. It pointed at `InboxPage` with `tab: 'sales'`, which is the
       * conversation side of the same transaction: the seller was told to fulfil
       * an order and landed on a chat window. */
      route: 'SalePage',
    });
  }

  /* ---- Unread messages ----------------------------------------------- */
  if (unreadMessageCount > 0) {
    rows.push({
      id: 'unread-messages',
      severity: SEVERITY.AMBER,
      titleKey: 'SellerDashboardPage.todo.unreadMessages.title',
      reasonKey: 'SellerDashboardPage.todo.unreadMessages.reason',
      reasonValues: { count: unreadMessageCount },
      actionKey: 'SellerDashboardPage.todo.unreadMessages.action',
      /* Messages DO belong in the inbox — and filtered to unread, so the page
       * opens showing the same rows the count is about. */
      route: 'InboxPage',
      routeParams: { tab: 'sales' },
      search: '?filter=unread',
    });
  }

  /* ---- Low stock ------------------------------------------------------
   * Amber: nothing is broken, but the listing is about to stop selling. */
  if (lowStockCount > 0) {
    rows.push({
      id: 'low-stock',
      severity: SEVERITY.AMBER,
      titleKey: 'SellerDashboardPage.todo.lowStock.title',
      reasonKey: 'SellerDashboardPage.todo.lowStock.reason',
      reasonValues: { count: lowStockCount },
      actionKey: 'SellerDashboardPage.todo.lowStock.action',
      route: 'ManageListingsPage',
    });
  }

  /* ---- Unfinished drafts ---------------------------------------------
   * Only worth mentioning once the seller is actually selling. For a brand-new
   * account the "Kom igång" checklist already covers the first listing, and
   * saying it twice makes the page feel like nagging. */
  if (draftCount > 0 && hasPublishedListing) {
    rows.push({
      id: 'drafts',
      severity: SEVERITY.AMBER,
      titleKey: 'SellerDashboardPage.todo.drafts.title',
      reasonKey: 'SellerDashboardPage.todo.drafts.reason',
      reasonValues: { count: draftCount },
      actionKey: 'SellerDashboardPage.todo.drafts.action',
      route: 'ManageListingsPage',
    });
  }

  /* Stable sort: severity first, insertion order within a severity. Array#sort
   * is not guaranteed stable across every engine for large arrays, but this list
   * is a handful of rows, and comparing on rank alone keeps insertion order in
   * every engine that implements the spec's stable sort (all current ones). */
  return rows.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
};

/**
 * Does this Stripe account still owe information before it can be paid?
 *
 * Mirrors hasOutstandingRequirements/needsOnboarding in
 * containers/StripeDashboardPage/stripeOnboardingStatus.js. Kept as a local
 * function rather than an import so this module stays free of page-specific
 * dependencies; the two are checked against each other by test.
 *
 * `eventually_due` is deliberately excluded — Stripe will ask for it later, and
 * it is not a reason to tell a seller their payouts are blocked today.
 */
export const needsStripeInfo = status => {
  if (!status) return false;
  if (!status.detailsSubmitted) return true;
  if (!status.hasExternalAccount) return true;
  return (status.pastDue?.length || 0) > 0 || (status.currentlyDue?.length || 0) > 0;
};

/** The most urgent row, or null. Drives the one-line banner in BR-4. */
export const mostUrgentRow = rows => (rows && rows.length ? rows[0] : null);

/** True when at least one row is severe enough to follow the seller around. */
export const hasUrgentAction = rows =>
  !!rows && rows.some(r => r.severity === SEVERITY.RED);
